import React, { useEffect, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export default function AdminAffiliate() {
  const [rows, setRows] = useState([]);
  const [rate, setRate] = useState(0.1);
  const load = () => {
    api.get("/admin/affiliate/commissions").then((r) => setRows(r.data));
    api.get("/admin/affiliate/rate").then((r) => setRate(r.data.rate));
  };
  useEffect(() => { load(); }, []);

  const saveRate = async () => {
    try { await api.put("/admin/affiliate/rate", { rate: Number(rate) }); toast.success("Rate updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const payout = async (id) => {
    try { await api.post("/admin/affiliate/payout", { commission_id: id }); toast.success("Marked paid"); load(); }
    catch { toast.error("Failed"); }
  };

  const totals = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + r.commission_usd;
    return acc;
  }, {});

  return (
    <AdminLayout>
      <h1 className="bx-heading text-3xl font-extrabold">Affiliate Program</h1>
      <p className="text-slate-400 text-sm mt-1">Manage commission rate and approve payouts.</p>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="bx-card p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Pending Payouts</div>
          <div className="bx-heading mt-2 text-3xl font-extrabold text-amber-300">${(totals.pending || 0).toFixed(2)}</div>
        </div>
        <div className="bx-card p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Paid</div>
          <div className="bx-heading mt-2 text-3xl font-extrabold text-emerald-400">${(totals.paid || 0).toFixed(2)}</div>
        </div>
        <div className="bx-card p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Total Commissions</div>
          <div className="bx-heading mt-2 text-3xl font-extrabold">{rows.length}</div>
        </div>
      </div>

      <div className="bx-card p-5 mt-6 max-w-md">
        <div className="bx-heading text-lg font-semibold">Commission Rate</div>
        <p className="text-xs text-slate-400 mt-1">Lifetime % applied to every paid order from referred users.</p>
        <div className="mt-3 flex gap-3 items-center">
          <input type="number" step="0.01" min="0" max="0.5" value={rate} onChange={(e) => setRate(e.target.value)} className="bx-input w-32" />
          <span className="text-sm text-slate-300">= {Math.round(rate * 100)}%</span>
          <button data-testid="admin-affiliate-save-rate" onClick={saveRate} className="bx-btn-primary"><Lucide.Save size={16} /> Save</button>
        </div>
      </div>

      <div className="mt-8">
        <div className="bx-heading text-lg font-semibold mb-3">All commissions</div>
        <div className="bx-card overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Affiliate</th>
                <th className="text-left p-3">Order amount</th>
                <th className="text-right p-3">Commission</th>
                <th className="text-right p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="p-3 text-slate-400">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="p-3">{c.affiliate_user || "—"}</td>
                  <td className="p-3">${c.amount_usd.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-emerald-400">${c.commission_usd.toFixed(2)}</td>
                  <td className="p-3 text-right"><span className="bx-chip" style={{ fontSize: 10 }}>{c.status}</span></td>
                  <td className="p-3 text-right">
                    {c.status === "pending" && (
                      <button onClick={() => payout(c.id)} className="bx-btn-ghost text-xs">Mark paid</button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No commissions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
